import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import React from "react";
import { Controller, get, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface SelectFieldProps {
  fieldName: string;
  label?: string;
  fullWidth?: boolean;
  control?: any;
  error?: boolean;
  helperText?: any;
  defaultValue?: string | boolean | null | number | any[];
  options: any[];
  variant?: "filled" | "outlined" | "standard";
  valueKey: string;
  labelKey: string;
  disabled?: boolean;
  onFieldChange?: (event: React.ChangeEvent<{ value: unknown }>) => void;
  other?: object;
  labelManipulateFn?: (label: string) => string;
  style?: object;
  onChangeFunction?: (option: any) => void;
  emptyOptionLabel?: string | null;
  multiple?: boolean; // NEW PROP
  renderOption?: (option: any) => React.ReactNode;
  renderValue?: (value: any) => React.ReactNode;
  showMissingDeleted?: boolean;
  currentValue?: any;
  showEmptyOption?: boolean;
  // optional: provide yup schema so component can show required asterisk on mount
  yupSchema?: any;
  required?: boolean;
}

const SelectField: React.FC<SelectFieldProps> = ({
  fieldName,
  control,
  label,
  error: errorBool = false,
  helperText,
  fullWidth,
  defaultValue,
  options,
  variant = "outlined",
  valueKey,
  labelKey,
  disabled,
  onFieldChange,
  other,
  labelManipulateFn,
  style,
  onChangeFunction,
  emptyOptionLabel = "None",
  multiple = false,
  renderOption = null,
  renderValue,
  showMissingDeleted = false,
  currentValue,
  showEmptyOption = true,
  yupSchema,
  required = false,
}) => {
  const methods = useFormContext();
  const { t } = useTranslation();

  const {
    control: formControl,
    watch,
    setValue: setField,
    getValues,
    trigger,
    formState: { errors },
  } = methods || {};

  // useEffect(() => {
  //   if (options?.length === 1 && !getValues(fieldName)) {
  //     setField?.(fieldName, options?.[0]?.[valueKey]);
  //   }
  // }, [options?.[0]]);

  const computeIsRequired = React.useCallback(() => {
    if (required) return true;
    try {
      if (yupSchema) {
        if (typeof yupSchema?.describe === "function") {
          const desc = yupSchema?.describe();
          const f = desc?.fields?.[fieldName];
          if (f?.tests)
            return f.tests?.some((t: any) => t?.name === "required");
        }
        const fieldSchema =
          yupSchema?.fields?.[fieldName] ?? yupSchema?.[fieldName];
        if (fieldSchema) {
          const tests = fieldSchema?.tests ?? fieldSchema?._tests ?? [];
          if (Array.isArray(tests))
            return tests?.some(
              (t: any) => t?.name === "required" || t?.TEST === "required",
            );
        }
      }
    } catch {
      /* ignore */
    }
    try {
      const fieldState = (methods as any)?.getFieldState
        ? (methods as any).getFieldState(fieldName)
        : null;
      return !!fieldState?.invalid;
    } catch {
      return false;
    }
  }, [required, yupSchema, fieldName, methods]);

  const [isRequiredState, setIsRequiredState] = React.useState<boolean>(() =>
    computeIsRequired(),
  );
  React.useEffect(() => {
    setIsRequiredState(computeIsRequired());
  }, [computeIsRequired]);

  const labelWithAsterisk = !label
    ? ""
    : isRequiredState
      ? `${label} *`
      : label;

  const emptyValue = emptyOptionLabel === "All" ? 0 : "";

  const isValueMissing =
    showMissingDeleted &&
    currentValue &&
    !multiple &&
    options &&
    !options?.some(
      (option) => String(option[valueKey]) === String(currentValue),
    );

  const watchedValue = watch ? watch(fieldName) : undefined;

  // Combine error messages
  const displayHelperText = isValueMissing ? "Missing/Deleted" : helperText;
  const rawError = errorBool || get(errors || {}, fieldName);

  // Determine touched/submitted state to control showing errors
  const fieldState = (methods as any)?.getFieldState
    ? (methods as any).getFieldState(fieldName)
    : null;
  const isTouched = !!(
    (methods?.formState &&
      methods.formState.touchedFields &&
      methods.formState.touchedFields[fieldName]) ||
    fieldState?.isTouched
  );

  const showError =
    !!rawError &&
    (isTouched || (methods?.formState && methods.formState.isSubmitted));

  const commonTextFieldStyles = {
    "& .MuiOutlinedInput-notchedOutline": {
      borderWidth: showError ? "0px" : "2px",
      borderColor: showError ? "transparent" : "primary.main",
      borderRadius: "8px",
      color: "secondary.main",
    },
    height: 38,
    backgroundColor: "rgba(63, 141, 124, 0.00)",
    "& .MuiSelect-select": {
      alignItems: "center",
      boxSizing: "border-box",
      display: "flex",
      height: "38px",
      minHeight: "0 !important",
      overflow: "hidden",
      padding: "7.5px 32px 7.5px 14px",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: showError ? "transparent" : "primary.main",
    },
    "& .MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.85)",
    },
    ...style,
  };

  React.useEffect(() => {
    if (!trigger || !watchedValue) return;
    trigger(fieldName);
  }, [watchedValue, fieldName, trigger]);

  const shouldShrink =
    (watchedValue !== "" &&
      watchedValue !== undefined &&
      watchedValue !== null &&
      (!Array.isArray(watchedValue) || watchedValue.length > 0)) ||
    !!(other && (other as any).displayEmpty);

  return (
    <FormControl
      fullWidth={fullWidth !== false}
      variant={variant}
      error={!!showError || isValueMissing}
      disabled={disabled ?? false}
    >
      {!!label && (
        <InputLabel
          shrink={shouldShrink}
          sx={{
            color:
              !!showError || isValueMissing ? "error.main" : "secondary.main",
            background: "none",
            padding: "0 5px",
          }}
        >
          {labelWithAsterisk}
        </InputLabel>
      )}
      <Controller
        name={fieldName}
        control={control ?? formControl}
        defaultValue={defaultValue || (multiple ? [] : emptyValue)}
        render={({ field }) => {
          const displayOptions = [...(options || [])];
          const selectedValues = Array.isArray(field.value) ? field.value : [];
          return (
            <>
              <Select
                {...field}
                multiple={multiple}
                notched={shouldShrink}
                value={
                  multiple
                    ? Array.isArray(field.value)
                      ? field.value
                      : []
                    : (field.value ?? emptyValue)
                }
                onChange={(event) => {
                  const rawValue = event.target.value;
                  const value =
                    multiple && typeof rawValue === "string"
                      ? rawValue.split(",")
                      : rawValue;
                  onFieldChange?.(value);
                  field.onChange(value);
                }}
                renderValue={
                  renderValue
                    ? renderValue
                    : multiple
                      ? (selected) => {
                          const selectedValues = Array.isArray(selected)
                            ? selected
                            : [];

                          return selectedValues
                            .map((selectedValue) => {
                              const option = displayOptions.find(
                                (item) =>
                                  String(item[valueKey]) ===
                                  String(selectedValue),
                              );

                              if (!option) return selectedValue;

                              return labelManipulateFn
                                ? labelManipulateFn(option[labelKey])
                                : option[labelKey];
                            })
                            .join(", ");
                        }
                      : undefined
                }
                sx={commonTextFieldStyles}
                label={labelWithAsterisk}
                {...other}
              >
                {!multiple &&
                  showEmptyOption &&
                  emptyOptionLabel && ( // Modified this line
                    <MenuItem value={emptyValue}>
                      {emptyOptionLabel === "None"
                        ? t("common.labels.none", "None")
                        : emptyOptionLabel === "All"
                          ? t("common.labels.all", "All")
                          : emptyOptionLabel}
                    </MenuItem>
                  )}
                {displayOptions?.map((option, index) => (
                  <MenuItem
                    key={`${option[valueKey]}-${index}`}
                    value={option[valueKey]}
                    onClick={() => onChangeFunction?.(option)}
                    sx={
                      option[labelKey] === "Missing/Deleted"
                        ? {
                            color: "error.main",
                            fontStyle: "italic",
                          }
                        : {}
                    }
                  >
                    {multiple && (
                      <Checkbox
                        checked={selectedValues.some(
                          (selectedValue) =>
                            String(selectedValue) === String(option[valueKey]),
                        )}
                        size="small"
                        sx={{ mr: 1, p: 0.25 }}
                      />
                    )}
                    {renderOption ? (
                      renderOption(option)
                    ) : (
                      <span>
                        {labelKey === "status"
                          ? t(
                              `common.vehicleStatus.${option[labelKey]}`,
                              option[labelKey],
                            )
                          : labelKey === "label"
                            ? t(
                                `options.fuelLevelOptions.${option[labelKey]}`,
                                option[labelKey],
                              )
                            : fieldName === "contractType"
                              ? t(
                                  `common.contractTypes.${option[labelKey]}`,
                                  option[labelKey],
                                )
                              : labelManipulateFn
                                ? labelManipulateFn(option[labelKey])
                                : option[labelKey]}
                      </span>
                    )}
                  </MenuItem>
                ))}
              </Select>
              {!!showError || isValueMissing ? (
                <FormHelperText
                  sx={{
                    color: "error.main",
                  }}
                >
                  {displayHelperText ??
                    get(errors || {}, fieldName)?.message?.toString()}
                </FormHelperText>
              ) : null}
            </>
          );
        }}
      />
    </FormControl>
  );
};

export default SelectField;
